import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import './Projects.css';

const PROJECTS = [
  {
    title: 'Transforming the onboarding experience for Experts',
    desc: 'To create a streamlined and scalable onboarding process for all experts on the Virtual Expert Platform (VEP) by overhauling the user experience (UX) to address current inefficiencies, providing clear task expectations and self-service support, and ensuring mobile-web responsiveness.',
    image: 'https://cdn.prod.website-files.com/62a9c118130e8b4c6d766b99/669d1d168f7f1d6ee95b0f5f_Group%201125087788%20(2).png',
    link: '/intuit-expert-portal',
  },
  {
    title: 'Improving the credit limit for Merchants',
    desc: 'Helping merchants to submit the most active business bank account for underwriting, resulting in a higher Credit limit for the customer.',
    image: 'https://cdn.prod.website-files.com/62a9c118130e8b4c6d766b99/64c8bb741cf9e6192e47672e_Group%2048095475.png',
    link: '/razorpay-capital',
    passwordProtected: true,
  },
  {
    title: "Rethinking MMT's Live Train Status for 25 million passengers",
    desc: "Redesigned MMT's Live Train Status feature in order to improve customer experience. Leveraging the power of Network Triangulation to serve its customers with more granular data.",
    image: 'https://cdn.prod.website-files.com/62a9c118130e8b4c6d766b99/62d576163f11a80fd593c553_Group%203782%20(2).png',
    link: '/mmt-live-train-status',
  },
  {
    title: 'Service design for business transformation at Wipro',
    desc: "Redesigned the Wipro deal pursuit experiences in order to support existing and future business models. Enable Wipro and its employees achieve their business goals through a more human-centric approach.",
    image: 'https://cdn.prod.website-files.com/62a9c118130e8b4c6d766b99/62d5619c89995117b2d231e2_Group%203774.png',
    link: '/wipro-deal-pricing-system',
  },
];

function PasswordDialog({ onSuccess, onClose }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === 'Raunak_2026') {
      onSuccess();
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
    }
  };

  return (
    <div className="password-overlay" onClick={onClose}>
      <div className="password-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="password-dialog-header">
          <span className="password-dialog-dot red" />
          <span className="password-dialog-dot yellow" />
          <span className="password-dialog-dot green" />
        </div>
        <div className="password-dialog-body">
          <h3 className="password-title">ACCESS RESTRICTED</h3>
          <p className="password-subtitle">authenticate to view case study</p>
          <form onSubmit={handleSubmit}>
            <div className="password-input-row">
              <span className="password-prompt">$</span>
              <input
                ref={inputRef}
                type="password"
                className={`password-input ${error ? 'password-error' : ''}`}
                placeholder="enter_password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="password-submit">Authenticate</button>
          </form>
          {error && <p className="password-error-text">access denied</p>}
        </div>
      </div>
    </div>
  );
}

function ProjectLink({ project, className, children, onPasswordClick, ...props }) {
  if (project.passwordProtected) {
    return <a href="#" onClick={(e) => { e.preventDefault(); onPasswordClick(); }} className={className} {...props}>{children}</a>;
  }
  const isExternal = project.link.startsWith('http');
  return <a href={isExternal ? project.link : process.env.PUBLIC_URL + project.link} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noreferrer' : undefined} className={className} {...props}>{children}</a>;
}

function MotionProjectLink({ project, className, children, onPasswordClick, ...motionProps }) {
  if (project.passwordProtected) {
    return (
      <motion.a href="#" onClick={(e) => { e.preventDefault(); onPasswordClick(); }} className={className} {...motionProps}>
        {children}
      </motion.a>
    );
  }
  const isExternal = project.link.startsWith('http');
  return (
    <motion.a href={isExternal ? project.link : process.env.PUBLIC_URL + project.link} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noreferrer' : undefined} className={className} {...motionProps}>
      {children}
    </motion.a>
  );
}

function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isEven = index % 2 === 0;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleDescClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePasswordSuccess = () => {
    setShowPassword(false);
    navigate(project.link);
  };

  return (
    <div className="project-section" ref={ref}>
      {showPassword && (
        <PasswordDialog
          onSuccess={handlePasswordSuccess}
          onClose={() => setShowPassword(false)}
        />
      )}
      <div className="project-container" data-outline>
        <div className={`project-wrapper ${isEven ? '' : 'reverse'}`} data-outline-h>
          {isEven ? (
            <>
              <motion.div
                className="project-content"
                initial={{ y: 70, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : { y: 70, opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <div className="project-title-wrap" data-outline-h>
                  <h1 className="project-title">{project.title}</h1>
                </div>
                <div className="project-desc-wrap" data-outline-h>
                  <div 
                    className={`project-desc ${isExpanded ? 'expanded' : 'truncated'}`}
                    onClick={handleDescClick}
                    role="button"
                    tabIndex="0"
                    onKeyPress={(e) => e.key === 'Enter' && handleDescClick()}
                  >
                    {project.desc}
                  </div>
                </div>
                <div className="project-desc-wrap desktop-only">
                  <ProjectLink project={project} className="project-arrow" onPasswordClick={() => setShowPassword(true)}>
                    <svg className="arrow-svg" width="48" height="48" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="16" /><line x1="12" y1="18" x2="24" y2="18" /><polyline points="19 13 24 18 19 23" /></svg>
                  </ProjectLink>
                </div>
              </motion.div>
              <MotionProjectLink
                project={project}
                className="project-image-link"
                onPasswordClick={() => setShowPassword(true)}
               
                initial={{ y: 70, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : { y: 70, opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
              >
                <img src={project.image} alt={project.title} className="project-image" loading="lazy" />
              </MotionProjectLink>
            </>
          ) : (
            <>
              <MotionProjectLink
                project={project}
                className="project-image-link"
                onPasswordClick={() => setShowPassword(true)}
               
                initial={{ y: 70, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : { y: 70, opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <img src={project.image} alt={project.title} className="project-image" loading="lazy" />
              </MotionProjectLink>
              <motion.div
                className="project-content"
                initial={{ y: 70, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : { y: 70, opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
              >
                <div className="project-title-wrap" data-outline-h>
                  <h1 className="project-title">{project.title}</h1>
                </div>
                <div className="project-desc-wrap" data-outline-h>
                  <div 
                    className={`project-desc ${isExpanded ? 'expanded' : 'truncated'}`}
                    onClick={handleDescClick}
                    role="button"
                    tabIndex="0"
                    onKeyPress={(e) => e.key === 'Enter' && handleDescClick()}
                  >
                    {project.desc}
                  </div>
                </div>
                <div className="project-desc-wrap desktop-only">
                  <ProjectLink project={project} className="project-arrow" onPasswordClick={() => setShowPassword(true)}>
                    <svg className="arrow-svg" width="48" height="48" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="16" /><line x1="12" y1="18" x2="24" y2="18" /><polyline points="19 13 24 18 19 23" /></svg>
                  </ProjectLink>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  return (
    <section id="projects">
      {PROJECTS.map((project, i) => (
        <ProjectCard key={i} project={project} index={i} />
      ))}
    </section>
  );
}
